/**
 * Generating and checking an opening password for a colleague.
 *
 * This password gets read out across a counter, written on a slip of paper, or
 * typed into a phone by somebody who was told it once. That shapes the alphabet
 * more than raw entropy does: `0`/`O` and `1`/`l`/`I` are dropped because the
 * failure they cause is a member who cannot sign in and a merchant who blames the
 * software, and mixed case is dropped because "capital B, small b" does not
 * survive being spoken.
 *
 * Sixteen characters from a 31-symbol alphabet is about 79 bits, which is far more
 * than a password nobody has to remember needs - it exists for a few minutes,
 * until the member chooses their own.
 */

import { DEFAULT_I18N, type Translator } from '@/lib/i18n';

/** Unambiguous when spoken, written by hand, or read off a screen. */
const ALPHABET = 'abcdefghjkmnpqrstuvwxyz23456789';

const LENGTH = 16;
/** Dashes every four characters, purely so it can be read back in chunks. */
const GROUP = 4;

/**
 * A random opening password, grouped for legibility.
 *
 * Uses the platform CSPRNG. `Math.random` would be indefensible here: this is a
 * credential, and its predictability is the whole question.
 */
export function generatePassword(): string {
  const picked = randomIndices(LENGTH, ALPHABET.length);
  const characters = [...picked].map((index) => ALPHABET[index]);

  const groups: string[] = [];
  for (let at = 0; at < characters.length; at += GROUP) {
    groups.push(characters.slice(at, at + GROUP).join(''));
  }
  return groups.join('-');
}

/**
 * `count` uniformly distributed indices below `ceiling`.
 *
 * Rejection sampling rather than `% ceiling`: the modulo is only unbiased when the
 * ceiling divides 256, which 31 does not, and it would quietly make the first few
 * letters of the alphabet likelier than the rest. Drawing a fresh byte instead
 * costs a handful of extra bytes and is correct for any alphabet, so changing
 * ALPHABET above can never reintroduce the bias.
 */
function randomIndices(count: number, ceiling: number): number[] {
  const limit = Math.floor(256 / ceiling) * ceiling;
  const indices: number[] = [];
  const buffer = new Uint8Array(count * 2);

  while (indices.length < count) {
    crypto.getRandomValues(buffer);
    for (const byte of buffer) {
      if (byte >= limit) continue;
      indices.push(byte % ceiling);
      if (indices.length === count) break;
    }
  }
  return indices;
}

/**
 * Why this password would be refused, or undefined when it is acceptable.
 *
 * Mirrors `validate_password_strength` and `PasswordField` on the backend so the
 * merchant is told in the form rather than by a 422 after they submit. The
 * backend remains the authority - this is a courtesy, not a check, and it is
 * deliberately no stricter, because a client that refused what the server accepts
 * would be a rule nobody could find.
 */
export function passwordProblem(value: string, t: Translator = DEFAULT_I18N.t): string | undefined {
  if (value.length < 10) return t('team.password.problems.too_short');
  if (value.length > 128) return t('team.password.problems.too_long');
  if (/^\d+$/.test(value)) return t('team.password.problems.only_numbers');
  if (new Set(value).size < 5) return t('team.password.problems.too_few_distinct');
  return undefined;
}
